import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, MapPin } from "lucide-react";
import { SERVICES } from "../lib/services";
import { QuoteModal } from "../components/QuoteModal";

export default function Pricing() {
  const [quoteService, setQuoteService] = useState(null);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="pricing-page">
      <div className="py-16">
        <div className="overline text-[var(--red)] mb-4">Pricing</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">
          Transparent <span className="text-[var(--red)]">ranges.</span> Tailored proposals when you need them.
        </h1>
        <p className="mt-6 text-lg text-[var(--text-2)] max-w-2xl">
          Every evaluation is scoped to your program, timeline, and funder requirements. Ranges below reflect typical
          engagement sizes — request a quote for a proposal tailored to your project.
        </p>
      </div>

      {/* $250 paid consultation — the only upfront checkout item */}
      <div
        className="card-dark p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-16"
        style={{ borderColor: "rgba(255,36,0,0.4)" }}
        data-testid="pricing-consultation-callout"
      >
        <div className="flex items-start gap-4">
          <MapPin className="w-8 h-8 text-[var(--red)] shrink-0 mt-1" />
          <div>
            <div className="overline text-[var(--silver)] mb-2">Instant booking</div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-1">Paid In-Person Consultation</h3>
            <p className="text-sm text-[var(--text-2)]">60 minutes · Las Vegas, NV · $250 flat — pay securely online, no quote needed.</p>
          </div>
        </div>
        <Link to="/book-consultation" className="btn-red shrink-0" data-testid="pricing-book-consultation-btn">
          Book &amp; Pay $250
        </Link>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s) => (
          <div key={s.id} className="card-dark p-8 flex flex-col" data-testid={`package-${s.id}`}>
            <s.icon className="w-7 h-7 text-[var(--red)] mb-5" />
            <div className="overline text-[var(--silver)] mb-2">{s.subtitle}</div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-3">{s.title}</h3>
            <div className="font-display text-2xl font-semibold mb-4" data-testid={`price-range-${s.id}`}>
              {s.priceRange}
            </div>
            <p className="text-sm text-[var(--text-2)] leading-relaxed mb-7 flex-1">{s.desc}</p>
            <button
              onClick={() => setQuoteService(s)}
              className="btn-red w-full justify-center"
              data-testid={`request-quote-${s.id}`}
            >
              Request a Quote
            </button>
          </div>
        ))}
      </div>

      <section className="mt-24 grid lg:grid-cols-2 gap-10 border border-[var(--border)] p-10 bg-[var(--surface)]">
        <div>
          <div className="overline text-[var(--red)] mb-3">Custom engagements</div>
          <h3 className="font-display text-3xl font-semibold tracking-tight mb-4">Need something bigger?</h3>
          <p className="text-[var(--text-2)] leading-relaxed mb-6">
            Multi-year evaluations, statewide CHNAs, or performance measurement systems — we scope and price these proposals individually. Once approved, we send invoices through your portal for secure payment.
          </p>
          <Link to="/contact" className="btn-red" data-testid="pricing-custom-btn">Request a Custom Proposal</Link>
        </div>
        <ul className="space-y-4 text-sm">
          {["Federal/state funder formats supported", "Net-30 invoice terms available", "Milestone-based billing on request", "Group rates for coalitions and consortia"].map((t) => (
            <li key={t} className="flex items-start gap-3 text-[var(--text-2)]"><Check className="w-4 h-4 text-[var(--red)] mt-0.5" /> {t}</li>
          ))}
        </ul>
      </section>

      {quoteService && <QuoteModal service={quoteService} onClose={() => setQuoteService(null)} />}
    </div>
  );
}
